import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import SquarePaymentForm from '../intake/SquarePaymentForm';
import { OTP_ENABLED } from '../intake/PhoneOtpGate';
import { MembershipNotice, ProviderReviewNotice } from '../intake/checkoutUi';
import ProductLearnMore from '../intake/ProductLearnMore';
import { useSchoolPrideConfetti } from '@/lib/confetti';
import { WL_ADDITIVES, ANSWER_TO_KEY, additiveOf, findCompound } from '@/lib/wlAdditives';
import { termOptions, termDays, fmtUsd, fmtUsdRound } from '@/lib/checkoutTerms';
import { apiService } from '@/services/api';
import { dexaAddOnCents, dexaAddOnPayload, dexaOfferAvailable } from '@/data/dexaAddOn';
import { conciergeAddOnAvailable, conciergeAddOnCents, conciergeAddOnPayload } from '@/data/conciergeAddOn';
import { otoCardOnFile } from '@/data/conciergeOto';
import OtoCardSaveNotice from '../intake/OtoCardSaveNotice';
import { DexaUpsell, ConciergeUpsell, AddOnLineItems } from '../intake/CheckoutUpsells';

// §glp1-preview checkout. Same catalog/additive/payment contract as
// GlpCatalogCheckoutStep (selectedMedIds → payment + create-visit-v2), plus an
// omzo-style TERM picker: monthly / 3 months / 6 months. Each term is a real
// catalog SKU for the same product (lib/checkoutTerms.js), so the charged
// amount and the BE billing cadence follow from the SKU's daySupply. A term
// with no SKU in the catalog is shown disabled rather than invented.

function resolveBase(meds, molecule, isNewPatient) {
  const byMol = molecule ? meds.filter((m) => `${m.name} ${m.medication}`.toLowerCase().includes(molecule)) : meds.slice();
  const pool = (byMol.length ? byMol : meds).filter((m) => !additiveOf(m));
  if (!pool.length) return null;
  const starting = pool.filter((m) => /starting dose/i.test(m.name || '')).sort((a, b) => (a.price || 0) - (b.price || 0));
  if (isNewPatient && starting.length) return starting[0];
  return pool.slice().sort((a, b) => (a.price || 0) - (b.price || 0))[0];
}
function chosenKeys(answers) {
  const v = answers?.['side-effects'];
  const map = v && v.additives ? v.additives : {};
  return new Set(Object.values(map).map((x) => ANSWER_TO_KEY[x]).filter(Boolean));
}
// Weight-journey outlook. This flow stores the weight under 'height-weight'
// (the live checkout reads 'weight-pounds', which /glp1 never sets).
function weightOutlook(answers) {
  const hw = answers?.['height-weight'];
  const current = Number(hw?.weight || 0);
  const goalStr = String(answers?.['weight-loss-goal'] || '');
  const nums = (goalStr.match(/\d+/g) || []).map(Number);
  const loss = nums.length ? Math.max(...nums) : 0;
  if (!current || !loss) return null;
  return { current, target: Math.max(0, current - loss), loss };
}

const Check = () => (
  <svg className="w-4 h-4 text-[#2d8a5f] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);
const MedImage = ({ item, size = 'w-16 h-16' }) => {
  const [failed, setFailed] = useState(false);
  if (item?.imageUrl && !failed) {
    return <img src={item.imageUrl} alt={item.name || 'Medication'} onError={() => setFailed(true)} className={`${size} rounded-xl object-cover flex-shrink-0 bg-[#f0faf4]`} />;
  }
  return (
    <div className={`${size} rounded-xl bg-gradient-to-br from-[#e8f5ee] to-[#cfeadd] flex items-center justify-center flex-shrink-0`} aria-hidden="true">
      <svg className="w-8 h-8 text-[#2d8a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 3h6M10 3v4.5L5.6 16a3 3 0 002.6 4.5h7.6a3 3 0 002.6-4.5L14 7.5V3M7.5 13h9" />
      </svg>
    </div>
  );
};

// Monthly / 3 / 6 / 12-month plan cards (omzo-style stacked layout). Prices
// and availability come only from the catalog (admin drug-catalog + tenant
// overlay): a term with no SKU renders as a price-less "Coming soon" card so
// the FE never shows a number the backend didn't set.
const TermPicker = ({ terms, selectedDays, onSelect }) => (
  <div className="space-y-3 mb-4" data-testid="term-picker">
    {terms.map((t) => {
      if (!t.available) {
        return (
          <div key={t.days} className="wl-term relative p-5 opacity-70" data-testid={`term-${t.days}-unavailable`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xl font-bold text-gray-900 leading-tight">{t.label}</p>
                <p className="text-sm text-gray-500">{t.supply}</p>
              </div>
              <span className="wl-badge" style={{ background: '#b8ab9d' }}>Coming soon</span>
            </div>
            <p className="mt-3 text-sm text-gray-500">Not yet offered for this medication.</p>
          </div>
        );
      }
      const sel = t.days === selectedDays;
      return (
        <div key={t.days} className={`wl-term relative p-5 ${sel ? 'is-selected' : ''}`} data-testid={`term-${t.days}`}>
          {t.badge && <span className="wl-badge absolute -top-3 left-5">{t.badge}</span>}
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xl font-bold text-gray-900 leading-tight">{t.label}</p>
              <p className="text-sm text-gray-500">{t.supply}</p>
            </div>
            {t.saveCents > 0 && (
              <span className="wl-save" data-testid={`term-${t.days}-save`}>You are saving {fmtUsdRound(t.saveCents)}</span>
            )}
          </div>
          <p className="mt-3 text-sm text-gray-700 flex gap-2"><span style={{ color: 'var(--wl-orange)' }}>✓</span> {t.line}</p>
          <button type="button" onClick={() => onSelect(t.days)} aria-pressed={sel}
            className={`mt-4 w-full rounded-2xl px-4 py-3.5 font-semibold transition-colors ${sel ? 'text-white' : ''}`}
            style={sel ? { background: 'var(--wl-orange)' } : { background: '#fff', border: '1.5px solid #ecdfd1', color: 'var(--wl-ink)' }}
            data-testid={`term-${t.days}-select`}>
            {sel ? 'Selected' : 'Select Plan'} • {fmtUsdRound(t.perMonthCents)} / month
          </button>
        </div>
      );
    })}
  </div>
);

const GlpPreviewCheckoutStep = ({
  onComplete, isProcessing, answers, setLoading, clearSavedProgress, primaryGoal, currentStep, timestamp, email,
}) => {
  const stagingTest =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('belugaEnv') === 'staging';

  const [wlMeds, setWlMeds] = useState(null);
  const [standaloneCat, setStandaloneCat] = useState([]);
  const [error, setError] = useState('');
  const [compoundKey, setCompoundKey] = useState(null);
  const [separateIds, setSeparateIds] = useState([]);
  const [manualBaseId, setManualBaseId] = useState(null);
  const [termSel, setTermSel] = useState(null); // 30 | 90 | 180, null → base's own term
  const [modalOpen, setModalOpen] = useState(false);
  // recommend → (dexa) → (concierge) → pay. The two upsell pages are optional
  // and flag/state-gated; payment is always last.
  const [phase, setPhase] = useState('recommend');
  const [dexaSessions, setDexaSessions] = useState(null); // 1 | 3 | null
  const [conciergeSel, setConciergeSel] = useState(false);
  const phoneVerifyToken = answers?.['basic-contact']?.phoneVerifyToken || undefined;

  useEffect(() => {
    if (!OTP_ENABLED) return;
    const c = answers?.['basic-contact'] || {};
    const s = answers?.['shipping-address'] || {};
    if (!c.email && !c.phone) return;
    apiService.captureLead({
      name: c.name, email: c.email, phone: c.phone,
      address1: s.address1, city: s.city, state: s.state, zip: s.zipCode,
      visitType: 'weightloss', flow: primaryGoal, lastStep: 'checkout',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const molecule = (answers?.['medication-selection'] || '').toLowerCase();
  const glpMeds = answers?.['current-glp-medications'];
  const isNewPatient = Array.isArray(glpMeds) ? glpMeds.includes('none') : true;
  const outlook = useMemo(() => weightOutlook(answers), [answers]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiService.getMedications({ visitType: 'weightloss' }),
      apiService.getMedications({ visitType: 'antiAging' }),
      apiService.getMedications({ visitType: 'antiNausea' }).catch(() => []),
    ])
      .then(([wl, aa, an]) => { if (!alive) return; setWlMeds(wl); setStandaloneCat([...wl, ...aa, ...an]); })
      .catch((e) => { console.error('catalog fetch failed', e); if (alive) setError('We could not load your treatment right now. Please try again.'); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [molecule]);

  // The product the patient is on (chosen from the modal, or resolved), before
  // the term is applied.
  const product = useMemo(() => {
    if (!wlMeds || !wlMeds.length) return null;
    if (manualBaseId) { const m = wlMeds.find((x) => x.medId === manualBaseId); if (m) return m; }
    return resolveBase(wlMeds, molecule, isNewPatient);
  }, [wlMeds, molecule, isNewPatient, manualBaseId]);

  const terms = useMemo(() => termOptions(product, wlMeds || []), [product, wlMeds]);
  const selectedDays = termSel && terms.find((t) => t.days === termSel && t.available) ? termSel : termDays(product?.daySupply);
  const selectedTerm = terms.find((t) => t.days === selectedDays && t.available) || null;
  // The SKU that represents the plan (charged + submitted).
  const base = useMemo(() => (selectedTerm ? selectedTerm.sku : product), [selectedTerm, product]);
  const cheapestPerMonth = terms.filter((t) => t.available).sort((a, b) => a.perMonthCents - b.perMonthCents)[0] || null;

  useSchoolPrideConfetti(phase === 'recommend' && !!base);

  const baseSkus = useMemo(
    () => (wlMeds || []).filter((m) => !additiveOf(m)).sort((a, b) => (a.name || '').localeCompare(b.name || '') || (a.price || 0) - (b.price || 0)),
    [wlMeds],
  );

  const additiveOptions = useMemo(() => {
    if (!base || !wlMeds) return [];
    return WL_ADDITIVES.map((a) => {
      const compound = findCompound(a, base, wlMeds);
      if (compound) return { ...a, type: 'compound', sku: compound, addedPrice: (compound.price || 0) - (base.price || 0) };
      const standalone = standaloneCat
        .filter((m) => a.standaloneRe.test((m.name || '').trim()) && !/semaglutide|tirzepatide/i.test(m.name || '') && m.requiresSeparateIntake !== true)
        .sort((x, y) => (x.price || 0) - (y.price || 0))[0];
      if (standalone) return { ...a, type: 'standalone', sku: standalone, addedPrice: standalone.price || 0 };
      return null;
    }).filter(Boolean);
  }, [base, wlMeds, standaloneCat]);

  useEffect(() => {
    if (!additiveOptions.length) return;
    const want = chosenKeys(answers);
    additiveOptions.forEach((o) => {
      if (!want.has(o.key)) return;
      if (o.type === 'compound') setCompoundKey((p) => p || o.key);
      else setSeparateIds((p) => (p.includes(o.sku.medId) ? p : [...p, o.sku.medId]));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additiveOptions]);

  const compoundOpt = additiveOptions.find((o) => o.type === 'compound' && o.key === compoundKey);
  const active = compoundOpt ? compoundOpt.sku : base;
  const requestedOpts = useMemo(() => additiveOptions.filter((o) => chosenKeys(answers).has(o.key)), [additiveOptions, answers]);
  const separateOpts = additiveOptions.filter((o) => o.type === 'standalone' && separateIds.includes(o.sku.medId));
  const primaryCents = active ? Number(active.price) || 0 : 0;
  const dexaCents = dexaAddOnCents(dexaSessions);
  const conciergeCents = conciergeAddOnCents(conciergeSel);
  const totalCents = primaryCents + separateOpts.reduce((s, o) => s + (Number(o.sku.price) || 0), 0) + dexaCents + conciergeCents;
  // BE #349: no DEXA step on a $0 order (never reaches the Square charge).
  const hasDexa = dexaOfferAvailable(totalCents - dexaCents);
  useEffect(() => { if (!hasDexa && dexaSessions) setDexaSessions(null); }, [hasDexa, dexaSessions]);
  const hasConcierge = conciergeAddOnAvailable(answers);
  const afterRecommend = hasDexa ? 'dexa' : hasConcierge ? 'concierge' : 'pay';
  const afterDexa = hasConcierge ? 'concierge' : 'pay';
  const beforePay = hasConcierge ? 'concierge' : hasDexa ? 'dexa' : 'recommend';
  const selectedMedIds = active ? [active.medId, ...separateOpts.map((o) => o.sku.medId)] : [];

  const toggle = (o) => {
    if (o.type === 'compound') setCompoundKey((prev) => (prev === o.key ? null : o.key));
    else setSeparateIds((prev) => (prev.includes(o.sku.medId) ? prev.filter((x) => x !== o.sku.medId) : [...prev, o.sku.medId]));
  };
  const isAdded = (o) => (o.type === 'compound' ? compoundKey === o.key : separateIds.includes(o.sku.medId));
  const additiveRow = (o) => {
    const added = isAdded(o);
    return (
      <div key={o.key} className="flex items-center gap-3">
        <MedImage item={o.sku} size="w-12 h-12" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-800">{o.label}</p>
          <p className="text-xs text-[#2d8a5f]">{o.addedPrice > 0 ? `+${fmtUsd(o.addedPrice)}` : 'Included'}{o.type === 'compound' ? ' · in your medication' : ''}</p>
          {o.type === 'standalone' && <p className="text-xs text-gray-400">Separate prescription — ordered together with your treatment</p>}
        </div>
        <button type="button" onClick={() => toggle(o)} data-testid={`addon-${o.key}`} aria-pressed={added}
          className={`text-sm font-medium rounded-full px-3 py-1.5 flex-shrink-0 transition-colors ${added ? 'bg-[#2d8a5f] text-white' : 'bg-[#e8f5ee] text-[#2d8a5f] hover:bg-[#d8efe2]'}`}>
          {added ? 'Added ✓' : '+ Add'}
        </button>
      </div>
    );
  };
  // Picking from the modal resets the term to that SKU's own length.
  const chooseBase = (medId) => { setManualBaseId(medId); setTermSel(null); setCompoundKey(null); setModalOpen(false); };
  const chooseTerm = (days) => { setTermSel(days); setCompoundKey(null); };
  const submit = (payment) => onComplete({
    ...payment, selectedMedIds, selectedMedId: active?.medId, termDays: selectedDays, termMonths: selectedTerm?.months, phoneVerifyToken: phoneVerifyToken || undefined,
    // Add-ons: absent when nothing was added (never present-and-zero).
    dexaAddOn: dexaAddOnPayload(dexaSessions) || undefined,
    conciergeAddOn: conciergeAddOnPayload(conciergeSel) || undefined,
  });

  if (error) return <p className="max-w-md mx-auto text-center text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="catalog-error">{error}</p>;
  if (wlMeds === null) return <p className="max-w-md mx-auto text-center text-gray-500 py-10" data-testid="catalog-loading">Loading your treatment…</p>;
  if (!base) return <p className="max-w-md mx-auto text-center text-gray-600 py-10" data-testid="catalog-empty">This treatment isn’t currently available. Please check back soon.</p>;

  const planModal = modalOpen && createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" data-testid="modify-plan-modal" onClick={() => setModalOpen(false)}>
      <div className="wl-preview bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="font-semibold text-gray-900">Choose your medication</h3>
            <p className="text-sm text-gray-500">Pick a different strength or brand. Your provider confirms the final choice.</p>
          </div>
          <button type="button" onClick={() => setModalOpen(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600 text-2xl leading-none px-2">×</button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-2">
          {baseSkus.map((m) => {
            const sel = m.medId === base.medId;
            return (
              <button key={m.medId} type="button" onClick={() => chooseBase(m.medId)} data-testid={`plan-${m.medId}`}
                className={`w-full text-left rounded-2xl border-2 p-3 flex items-center gap-3 transition-all ${sel ? 'border-[#2d8a5f] bg-[#f0faf4]' : 'border-gray-200 hover:border-gray-300'}`}>
                <MedImage item={m} size="w-11 h-11" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 leading-snug">{m.name || m.medication}</p>
                  <p className="text-xs text-gray-500">{[m.strength, m.daySupply ? `${m.daySupply}-day` : null].filter(Boolean).join(' · ')}</p>
                </div>
                <span className="text-sm font-bold text-gray-900 flex-shrink-0">{fmtUsd(m.price)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );

  // ---------- PHASE 1: RECOMMENDATION ----------
  if (phase === 'recommend') {
    return (
      <div data-testid="glp-recommend" className="max-w-md mx-auto">
        <div className="mb-6">
          <p className="text-[#2d8a5f] font-semibold text-sm mb-1">Congratulations!</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">Your provider will likely recommend treatment.</h2>
          <p className="text-gray-500 text-sm mt-2">Select the treatment you'd like your medical provider to consider prescribing.</p>
        </div>

        <div className="rounded-3xl border-2 border-[#2d8a5f] bg-[#f0faf4] p-5 mb-4">
          <div className="flex items-center gap-4">
            <MedImage item={active} size="w-20 h-20" />
            <div className="min-w-0 flex-1">
              <p className="font-bold text-gray-900 leading-snug">{active.name || active.medication}</p>
              <p className="text-sm text-gray-500">{active.strength}</p>
              <p className="text-lg font-bold text-[#2d8a5f] mt-1">
                {cheapestPerMonth ? <>from {fmtUsdRound(cheapestPerMonth.perMonthCents)}<span className="text-sm font-medium">/mo</span></> : fmtUsd(active.price)}
              </p>
              <ProductLearnMore product={active} className="mt-1 inline-block" />
            </div>
          </div>
        </div>

        {requestedOpts.length > 0 && (
          <div className="rounded-2xl border border-gray-200 p-5 mb-4" data-testid="recommend-addons">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Based on your answers</p>
            <p className="text-xs text-gray-400 mb-3">You asked about these — include them for your provider to consider alongside your treatment.</p>
            <div className="space-y-3">{requestedOpts.map(additiveRow)}</div>
          </div>
        )}

        <button type="button" data-testid="select-treatment" onClick={() => setPhase(afterRecommend)} className="btn-primary mb-2">
          Select this treatment
        </button>
        <button type="button" onClick={() => setModalOpen(true)} data-testid="modify-plan" className="w-full text-center text-sm text-[#2d8a5f] underline underline-offset-2 hover:text-[#236b4a] mb-6">
          See other options
        </button>

        <div className="rounded-2xl bg-gray-50 p-5 mb-4">
          <h3 className="font-semibold text-gray-900 mb-3 text-sm">About your medication</h3>
          <ul className="space-y-2.5 text-sm text-gray-600">
            <li className="flex gap-2"><Check /> Every active ingredient is reviewed through our quality &amp; compliance program</li>
            <li className="flex gap-2"><Check /> Custom-formulated by licensed U.S. pharmacies</li>
            <li className="flex gap-2"><Check /> Physician oversight with dosing personalized to you</li>
          </ul>
        </div>

        {outlook && (
          <div className="rounded-2xl bg-gradient-to-br from-[#f0faf4] to-white border border-[#2d8a5f]/15 p-5 text-center" data-testid="outlook">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Your health journey outlook</p>
            <p className="text-4xl font-extrabold text-[#2d8a5f] leading-none">{outlook.target}<span className="text-lg font-semibold text-gray-400"> lbs</span></p>
            <p className="text-sm text-gray-500 mt-1">is achievable — about {outlook.loss} lbs from your current {outlook.current} lbs.</p>
            <div className="mt-4 h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#2d8a5f] to-[#4cb37e]" style={{ width: `${Math.min(100, Math.round((outlook.loss / outlook.current) * 100) * 3)}%` }} />
            </div>
          </div>
        )}
        {planModal}
      </div>
    );
  }

  // ---------- UPSELL PAGES (optional) ----------
  if (phase === 'dexa') {
    return (
      <div data-testid="glp-dexa-upsell" className="max-w-md mx-auto">
        <div className="mb-5">
          <p className="text-[#2d8a5f] font-semibold text-sm">One more thing</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">See the fat you're losing — not just the pounds.</h2>
        </div>
        <DexaUpsell sessions={dexaSessions} setSessions={setDexaSessions} onContinue={() => setPhase(afterDexa)} onBack={() => setPhase('recommend')} />
      </div>
    );
  }
  if (phase === 'concierge') {
    return (
      <div data-testid="glp-concierge-upsell" className="max-w-md mx-auto">
        <div className="mb-5">
          <p className="text-[#2d8a5f] font-semibold text-sm">Optional upgrade</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">Want a doctor on call while you lose the weight?</h2>
        </div>
        <ConciergeUpsell selected={conciergeSel} setSelected={setConciergeSel} onContinue={() => setPhase('pay')} onBack={() => setPhase(hasDexa ? 'dexa' : 'recommend')} />
      </div>
    );
  }

  // ---------- FINAL PHASE: PLAN + PAYMENT ----------
  return (
    <div data-testid="glp-preview-checkout" className="max-w-md mx-auto">
      <div className="mb-5">
        <p className="text-[#2d8a5f] font-semibold text-sm">You're in!</p>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">Choose your plan.</h2>
        <p className="text-sm text-gray-500 mt-1">Longer plans lower your monthly cost. Every plan includes provider review and free shipping.</p>
      </div>

      <TermPicker terms={terms} selectedDays={selectedDays} onSelect={chooseTerm} />

      <div className="rounded-3xl border border-[#2d8a5f]/30 bg-[#f0faf4] p-5 mb-3">
        <div className="flex items-center gap-4 pb-4 border-b border-[#2d8a5f]/15">
          <MedImage item={active} size="w-16 h-16" />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-gray-900 leading-snug" data-testid="primary-name">{active.name || active.medication}</p>
            <p className="text-sm text-gray-500">{[active.strength, selectedTerm ? selectedTerm.label : (active.daySupply ? `${active.daySupply}-day supply` : null)].filter(Boolean).join(' · ')}</p>
            <ProductLearnMore product={active} className="mt-0.5 inline-block" />
          </div>
          <span className="font-bold text-gray-900 flex-shrink-0">{fmtUsd(primaryCents)}</span>
        </div>
        <ul className="grid grid-cols-1 gap-2 pt-4 text-sm text-gray-700">
          <li className="flex gap-2"><Check /> Provider review &amp; prescription included</li>
          <li className="flex gap-2"><Check /> Free, discreet shipping</li>
          <li className="flex gap-2"><Check /> Unlimited care-team messaging</li>
          <li className="flex gap-2"><Check /> No long-term contract — cancel anytime</li>
        </ul>
        <button type="button" onClick={() => setModalOpen(true)} data-testid="modify-plan" className="mt-3 text-sm text-[#2d8a5f] underline underline-offset-2 hover:text-[#236b4a]">
          Modify your medication
        </button>
      </div>

      <ProviderReviewNotice className="mb-4" />
      <div className="flex items-center gap-3 rounded-2xl bg-[#fff7ed] border border-[#fed7aa] p-4 mb-4">
        <div className="w-9 h-9 rounded-full bg-[#fed7aa] flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-[#c2410c]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
        </div>
        <p className="text-sm text-gray-700"><span className="font-semibold">Free expedited shipping</span> — your treatment ships in 2–3 days once your provider prescribes it.</p>
      </div>

      {additiveOptions.length > 0 && (
        <div className="rounded-2xl border border-gray-200 p-5 mb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Frequently added together</p>
          <p className="text-xs text-gray-400 mb-3">Compounded into your medication or prescribed alongside it — no extra visit needed.</p>
          <div className="space-y-3">{additiveOptions.map(additiveRow)}</div>
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 p-5 mb-4">
        {separateOpts.map((o) => (
          <div key={o.sku.medId} className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">{o.label}</span><span className="font-medium">{fmtUsd(o.sku.price)}</span>
          </div>
        ))}
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-600">{active.name || active.medication}{selectedTerm && selectedTerm.months > 1 ? ` · ${selectedTerm.label}` : ''}</span><span className="font-medium">{fmtUsd(primaryCents)}</span>
        </div>
        <AddOnLineItems dexaSessions={dexaSessions} dexaCents={dexaCents} conciergeCents={conciergeCents} />
        <MembershipNotice />
        <div className="border-t border-gray-200 mt-2 pt-3 flex justify-between items-center">
          <span className="font-semibold text-lg">Total today</span>
          <span className="text-2xl font-bold text-[#2d8a5f]" data-testid="cart-total">{fmtUsd(totalCents)}</span>
        </div>
        {selectedTerm && selectedTerm.months > 1 && (
          <p className="text-xs text-gray-500 mt-1 text-right">≈ {fmtUsdRound(selectedTerm.perMonthCents)}/mo over {selectedTerm.months} months</p>
        )}
      </div>

      <div className="rounded-2xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Payment details</h3>
        {stagingTest ? (
          <div className="space-y-3">
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              ⚠️ Staging sign-off mode — Square is bypassed. Submits a test visit to Beluga <strong>staging</strong> with no charge.
            </p>
            <button type="button" data-testid="staging-bypass-pay" disabled={isProcessing} onClick={() => submit({ paymentId: 'STAGING_TEST_BYPASS' })} className="btn-primary">
              {isProcessing ? 'Submitting…' : `[Staging test] Skip payment & submit (${fmtUsd(totalCents)})`}
            </button>
          </div>
        ) : (
          <>
          {/* §concierge-oto: same card-save notice + card-keep signal as
              CatalogCheckoutStep — this is the live /glp1 checkout. */}
          <OtoCardSaveNotice answers={answers} visitType="weightloss" />
          <SquarePaymentForm planId={null} selectedMedIds={selectedMedIds} phoneVerifyToken={phoneVerifyToken} amount={totalCents} onComplete={submit}
            cardOnFile={conciergeSel || otoCardOnFile(answers, 'weightloss')} otoCardOnFile={otoCardOnFile(answers, 'weightloss')}
            dexaAddOn={hasDexa ? dexaAddOnPayload(dexaSessions) : null} nonDiscountableCents={hasDexa ? dexaCents : 0}
            answers={answers} setLoading={setLoading} clearSavedProgress={clearSavedProgress} primaryGoal={primaryGoal}
            currentStep={currentStep} timestamp={timestamp} email={email} />
          </>
        )}
      </div>
      <button type="button" onClick={() => setPhase(beforePay)} data-testid="pay-back" className="w-full mt-3 text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700">
        Back
      </button>
      {planModal}
    </div>
  );
};

export default GlpPreviewCheckoutStep;
